<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  objetive: {
    type: Object,
    default: () => ({})
  },
  darkmode: {
    type: Boolean,
    default: false
  }
});

const getContrastYIQ = (hexcolor = '#12395f') => {
  const safe = String(hexcolor || '#12395f');
  const normalized = safe.startsWith('#') ? safe : `#${safe}`;
  const r = parseInt(normalized.substring(1, 3), 16);
  const g = parseInt(normalized.substring(3, 5), 16);
  const b = parseInt(normalized.substring(5, 7), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return yiq >= 128 ? 'black' : 'white';
};

const formatMoney = (value) => {
  const total = parseFloat(value || 0);
  return total.toLocaleString('pt-br', {
    style: 'currency',
    currency: 'BRL',
  });
};

const progressPercent = computed(() => {
  const target = parseFloat(props.objetive?.target || 0);
  const balance = parseFloat(props.objetive?.balance || 0);

  if (!target || target <= 0) return 0;
  const percent = (balance / target) * 100;
  return Math.max(0, Math.min(percent, 100));
});

const progressLabel = computed(() => `${Math.round(progressPercent.value)}%`);

const progressRemaining = computed(() => {
  const target = parseFloat(props.objetive?.target || 0);
  const balance = parseFloat(props.objetive?.balance || 0);
  return Math.max(target - balance, 0);
});

const remainingTone = computed(() => {
  const percent = progressPercent.value;
  if (percent >= 90) return 'remaining-good';
  if (percent >= 60) return 'remaining-mid';
  return 'remaining-default';
});

const remainingLabel = computed(() => {
  const remaining = progressRemaining.value;

  if (remaining === 0) return 'Concluído 🎉';

  return `Faltam ${formatMoney(remaining)}`;
});

</script>

<template>
  <div class="objective-card" :class="{ 'objective-card--dark': darkmode }">
    <div class="objective-card__header">
      <div class="objective-card__identity">
        <div
          class="objective-card__icon"
          :style="{ color: objetive.color, backgroundColor: `${objetive.color}20` }"
        >
          <i
            :class="'fas ' + (objetive.icon || 'fa-bullseye') + ' objective-card__icon-symbol'"
          ></i>
        </div>

        <div class="objective-card__title-wrap">
          <p class="objective-card__title mb-0">
            {{ objetive.name }}
          </p>
        </div>
      </div>

      <div class="objective-card__badge">
        {{ progressLabel }}
      </div>
    </div>

    <div class="objective-card__content">
      <div class="objective-card__meta">
        <div class="objective-card__meta-item">
          <span class="objective-card__meta-label">{{ t('message.global.balance') }}</span>
          <strong class="objective-card__meta-value">
            {{ formatMoney(parseFloat(objetive.balance || 0)) }}
          </strong>
        </div>

        <div class="objective-card__meta-item objective-card__meta-item--right">
          <span class="objective-card__meta-label">{{ t('message.global.objetive', 1) }}</span>
          <strong class="objective-card__meta-value">
            {{ formatMoney(objetive.target || 0) }}
          </strong>
        </div>
      </div>

      <div class="objective-card__progress-wrap">
        <div
          class="progress objective-card__progress"
          role="progressbar"
          :aria-valuenow="progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="progress-bar objective-card__progress-bar"
            :style="'width:' + progressPercent + '%; background-color:' + (objetive.color || '#12395f')"
          ></div>
        </div>
      </div>

      <div class="objective-card__remaining" :class="remainingTone">
        {{ remainingLabel }}
      </div>

    </div>
  </div>
</template>

<style scoped>
.objective-card {
  border-radius: 22px;
  padding: 1rem 1rem 0.95rem;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.objective-card:hover {
  background: rgba(18, 57, 95, 0.035);
  transform: translateY(-1px);
}

.objective-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
}

.objective-card__identity {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.objective-card__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.10);
  flex: 0 0 auto;
}

.objective-card__icon-symbol {
  font-size: 1.1rem;
}

.objective-card__title-wrap {
  min-width: 0;
}

.objective-card__title {
  color: #31486d;
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.15;
}

.objective-card__subtitle {
  color: #7b8faa;
  font-size: 0.8rem;
  font-weight: 700;
}

.objective-card__subtitle.remaining-good {
  color: #198754;
}

.objective-card__subtitle.remaining-mid {
  color: #b46d2f;
}

.objective-card__subtitle.remaining-default {
  color: #7b8faa;
}

.objective-card__badge {
  border-radius: 999px;
  padding: 0.35rem 0.65rem;
  background: rgba(18, 57, 95, 0.08);
  color: #12395f;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.objective-card__content {
  margin-top: 0.95rem;
}

.objective-card__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.objective-card__meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.objective-card__meta-item--right {
  text-align: right;
}

.objective-card__meta-label {
  color: #7b8faa;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.1;
}

.objective-card__meta-value {
  color: #17212f;
  font-size: 0.92rem;
  font-weight: 800;
  line-height: 1.1;
}

.objective-card__progress-wrap {
  margin-top: 0.85rem;
}

.objective-card__progress {
  height: 8px;
  border-radius: 999px;
  background: #e9eef5;
  overflow: hidden;
}

.objective-card__progress-bar {
  border-radius: 999px;
}

.objective-card__footer {
  margin-top: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.objective-card__footer-label {
  color: #7b8faa;
  font-size: 0.78rem;
  font-weight: 600;
}

.objective-card__footer-value {
  color: #31486d;
  font-size: 0.88rem;
  font-weight: 800;
}

.objective-card--dark .objective-card__title,
.objective-card--dark .objective-card__meta-value,
.objective-card--dark .objective-card__footer-value {
  color: #f8fafc;
}

.objective-card--dark .objective-card__subtitle,
.objective-card--dark .objective-card__meta-label,
.objective-card--dark .objective-card__footer-label {
  color: #94a3b8;
}

.objective-card--dark .objective-card__subtitle.remaining-good {
  color: #4ade80;
}

.objective-card--dark .objective-card__subtitle.remaining-mid {
  color: #fbbf24;
}

.objective-card--dark .objective-card__subtitle.remaining-default {
  color: #94a3b8;
}

.objective-card--dark .objective-card__badge {
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
}

.objective-card--dark .objective-card__progress {
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 767.98px) {
  .objective-card {
    padding: 0.95rem 0.9rem 0.9rem;
  }

  .objective-card__icon {
    width: 42px;
    height: 42px;
  }

  .objective-card__title {
    font-size: 0.94rem;
  }

  .objective-card__meta-value {
    font-size: 0.88rem;
  }
}

.objective-card__remaining {
  margin-top: 0.5rem;
  font-size: 0.82rem;
  font-weight: 700;
  text-align: right;
}

</style>
